// @ts-nocheck
import { useState, useCallback } from 'react';
import { Stack, Title, Text, Code, Group, Button, Divider, Tabs, Box, Alert } from '@mantine/core';
import { IconCode, IconPlayerPlay, IconDownload, IconInfoCircle, IconForms } from '@tabler/icons-react';
import { Card } from '../../../components/ui';
import { FormBuilder } from '../../../components/advanced/form-builder';
import type { FormDefinition } from '../../../components/advanced/form-builder/types';

// Sample form for demonstration
const SAMPLE_FORM: FormDefinition = {
  id: 'sample_form',
  name: 'Contact Form',
  description: 'A sample contact form demonstrating form builder capabilities',
  fields: [
    {
      id: 'field_name',
      name: 'full_name',
      type: 'text',
      label: 'Full Name',
      placeholder: 'Enter your full name',
      required: true,
      colSpan: 2,
    },
    {
      id: 'field_email',
      name: 'email',
      type: 'email',
      label: 'Email Address',
      placeholder: 'your@email.com',
      required: true,
      validation: [{ type: 'email', message: 'Please enter a valid email' }],
    },
    {
      id: 'field_phone',
      name: 'phone',
      type: 'text',
      label: 'Phone Number',
      placeholder: '(555) 123-4567',
    },
    {
      id: 'field_subject',
      name: 'subject',
      type: 'select',
      label: 'Subject',
      placeholder: 'Select a subject',
      required: true,
      options: [
        { value: 'general', label: 'General Inquiry' },
        { value: 'support', label: 'Technical Support' },
        { value: 'sales', label: 'Sales Question' },
        { value: 'feedback', label: 'Feedback' },
      ],
    },
    {
      id: 'field_priority',
      name: 'priority',
      type: 'radio',
      label: 'Priority',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
      defaultValue: 'medium',
    },
    {
      id: 'field_message',
      name: 'message',
      type: 'textarea',
      label: 'Message',
      placeholder: 'Enter your message here...',
      required: true,
      rows: 5,
      colSpan: 2,
    },
    {
      id: 'field_subscribe',
      name: 'subscribe',
      type: 'checkbox',
      label: 'Subscribe to newsletter',
      defaultValue: false,
    },
  ],
  settings: {
    columns: 2,
    gap: 'md',
    submitLabel: 'Send Message',
    showReset: true,
    resetLabel: 'Clear Form',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export default function FormBuilderPage() {
  const [activeTab, setActiveTab] = useState<string | null>('builder');
  const [savedForm, setSavedForm] = useState<FormDefinition | null>(null);
  const [showJson, setShowJson] = useState(false);

  const handleSave = useCallback((form: FormDefinition) => {
    setSavedForm(form);
    console.log('Form saved:', form);
  }, []);

  const handleCancel = useCallback(() => {
    console.log('Form builder cancelled');
  }, []);

  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Group gap="sm">
          <IconForms size={28} />
          <Title order={2}>Form Builder</Title>
        </Group>
        <Text c="dimmed" size="sm">
          Visual drag-and-drop form builder with 20+ field types, validation rules, conditional logic, and live preview
        </Text>
      </Stack>

      {/* Info Alert */}
      <Alert icon={<IconInfoCircle size={16} />} title="About the Form Builder" color="blue">
        The Form Builder allows you to create dynamic forms visually. Drag fields from the sidebar,
        configure their properties, add validation rules, and set up conditional visibility.
        Export your forms as JSON for use in your applications.
      </Alert>

      {/* Main Content */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="builder" leftSection={<IconForms size={16} />}>
            Form Builder
          </Tabs.Tab>
          <Tabs.Tab value="docs" leftSection={<IconCode size={16} />}>
            Documentation
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="builder" pt="md">
          <Card withBorder p={0} style={{ height: 700, overflow: 'hidden' }}>
            <FormBuilder
              initialForm={SAMPLE_FORM}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </Card>

          {/* Saved Form JSON */}
          {savedForm && (
            <Card withBorder p="lg" mt="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Title order={4}>Saved Form Data</Title>
                  <Group gap="xs">
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconCode size={16} />}
                      onClick={() => setShowJson(!showJson)}
                    >
                      {showJson ? 'Hide' : 'Show'} JSON
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconDownload size={16} />}
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(savedForm, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${savedForm.name.toLowerCase().replace(/\s+/g, '-')}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download JSON
                    </Button>
                  </Group>
                </Group>

                {showJson && (
                  <Code block style={{ maxHeight: 400, overflow: 'auto' }}>
                    {JSON.stringify(savedForm, null, 2)}
                  </Code>
                )}

                <Text size="sm" c="dimmed">
                  Form "{savedForm.name}" with {savedForm.fields.length} fields saved successfully.
                </Text>
              </Stack>
            </Card>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="docs" pt="md">
          <Stack gap="lg">
            {/* Basic Usage */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Basic Usage</Title>
                <Text size="sm" c="dimmed">
                  Import and use the FormBuilder component in your application
                </Text>
                <Code block>
{`import { FormBuilder } from '@/components/advanced/form-builder';
import type { FormDefinition } from '@/components/advanced/form-builder/types';

function MyPage() {
  const handleSave = (form: FormDefinition) => {
    // Save form to your backend
    console.log('Form saved:', form);
  };

  return (
    <FormBuilder
      initialForm={existingForm}  // Optional: load existing form
      onSave={handleSave}
      onCancel={() => navigate('/forms')}
    />
  );
}`}
                </Code>
              </Stack>
            </Card>

            {/* Available Field Types */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Available Field Types</Title>
                <Text size="sm" c="dimmed">
                  The form builder supports 20+ field types organized by category
                </Text>

                <Stack gap="sm">
                  <Text size="sm" fw={500}>Basic Fields:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>text</Code> - Single line text input</li>
                    <li><Code>email</Code> - Email address input with validation</li>
                    <li><Code>password</Code> - Password input with masking</li>
                    <li><Code>number</Code> - Numeric input with min/max support</li>
                    <li><Code>textarea</Code> - Multi-line text input</li>
                  </Text>

                  <Text size="sm" fw={500}>Selection Fields:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>select</Code> - Single-select dropdown</li>
                    <li><Code>multiselect</Code> - Multi-select dropdown</li>
                    <li><Code>checkbox</Code> - Single checkbox toggle</li>
                    <li><Code>switch</Code> - Toggle switch</li>
                    <li><Code>radio</Code> - Radio button group</li>
                  </Text>

                  <Text size="sm" fw={500}>Date & Time:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>date</Code> - Date picker</li>
                    <li><Code>datetime</Code> - Date and time picker</li>
                    <li><Code>time</Code> - Time picker</li>
                  </Text>

                  <Text size="sm" fw={500}>Media & Files:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>file</Code> - File upload</li>
                    <li><Code>image</Code> - Image upload with preview</li>
                  </Text>

                  <Text size="sm" fw={500}>Advanced:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>slider</Code> - Range slider input</li>
                    <li><Code>rating</Code> - Star rating input</li>
                    <li><Code>richtext</Code> - Rich text editor (WYSIWYG)</li>
                    <li><Code>color</Code> - Color picker</li>
                  </Text>

                  <Text size="sm" fw={500}>Layout Elements:</Text>
                  <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                    <li><Code>heading</Code> - Section heading (h1-h6)</li>
                    <li><Code>paragraph</Code> - Static text paragraph</li>
                    <li><Code>divider</Code> - Horizontal divider line</li>
                    <li><Code>spacer</Code> - Vertical spacing</li>
                  </Text>
                </Stack>
              </Stack>
            </Card>

            {/* Validation Rules */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Validation Rules</Title>
                <Text size="sm" c="dimmed">
                  Add validation rules to ensure data quality
                </Text>
                <Code block>
{`// Available validation types
{
  type: 'required',    // Field must have a value
  type: 'email',       // Must be valid email format
  type: 'url',         // Must be valid URL format
  type: 'minLength',   // Minimum character count
  type: 'maxLength',   // Maximum character count
  type: 'min',         // Minimum numeric value
  type: 'max',         // Maximum numeric value
  type: 'pattern',     // Custom regex pattern
}

// Example field with validation
{
  name: 'username',
  type: 'text',
  label: 'Username',
  validation: [
    { type: 'required', message: 'Username is required' },
    { type: 'minLength', value: 3, message: 'At least 3 characters' },
    { type: 'maxLength', value: 20, message: 'Maximum 20 characters' },
    { type: 'pattern', value: '^[a-z0-9_]+$', message: 'Only lowercase letters, numbers, underscores' },
  ]
}`}
                </Code>
              </Stack>
            </Card>

            {/* Conditional Logic */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Conditional Logic</Title>
                <Text size="sm" c="dimmed">
                  Show or hide fields based on other field values
                </Text>
                <Code block>
{`// Conditional operators
'eq'          // Equal to
'neq'         // Not equal to
'gt'          // Greater than
'lt'          // Less than
'gte'         // Greater than or equal
'lte'         // Less than or equal
'contains'    // String contains
'notContains' // String does not contain
'isEmpty'     // Field is empty
'isNotEmpty'  // Field is not empty

// Example: Show "Other" field when "Type" is "other"
{
  name: 'other_reason',
  type: 'text',
  label: 'Please specify',
  showWhen: [
    { field: 'type', operator: 'eq', value: 'other' }
  ],
  showWhenLogic: 'and'  // or 'or' for multiple conditions
}`}
                </Code>
              </Stack>
            </Card>

            {/* Keyboard Shortcuts */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Keyboard Shortcuts</Title>
                <Text size="sm" c="dimmed">
                  Speed up your workflow with keyboard shortcuts
                </Text>
                <Text size="sm" component="ul" style={{ paddingLeft: '1.5em', margin: 0 }}>
                  <li><Code>Ctrl+Z</Code> / <Code>Cmd+Z</Code> - Undo last action</li>
                  <li><Code>Ctrl+Y</Code> / <Code>Cmd+Shift+Z</Code> - Redo last action</li>
                  <li><Code>Delete</Code> / <Code>Backspace</Code> - Delete selected field</li>
                  <li><Code>Escape</Code> - Clear selection</li>
                </Text>
              </Stack>
            </Card>

            {/* Form Schema */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Form Schema (FormDefinition)</Title>
                <Text size="sm" c="dimmed">
                  The complete TypeScript interface for form definitions
                </Text>
                <Code block>
{`interface FormDefinition {
  id: string;
  name: string;
  description?: string;
  fields: FormFieldDefinition[];
  settings: FormSettings;
  createdAt?: string;
  updatedAt?: string;
}

interface FormSettings {
  columns: 1 | 2 | 3 | 4;
  gap: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  submitLabel?: string;
  showReset?: boolean;
  resetLabel?: string;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

interface FormFieldDefinition {
  id: string;
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  description?: string;
  helpText?: string;
  required?: boolean;
  disabled?: boolean;
  defaultValue?: unknown;
  options?: SelectOption[];
  validation?: ValidationRule[];
  showWhen?: FieldCondition[];
  showWhenLogic?: 'and' | 'or';
  colSpan?: 1 | 2 | 3 | 4;
  // ... additional type-specific properties
}`}
                </Code>
              </Stack>
            </Card>

            {/* Using with DynamicForm */}
            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>Rendering Forms with DynamicForm</Title>
                <Text size="sm" c="dimmed">
                  Use the form definition with DynamicForm component
                </Text>
                <Code block>
{`import { DynamicForm } from '@/components/advanced/forms';
import { toFormFieldConfigs, getInitialValues } from '@/components/advanced/form-builder/utils';

function RenderForm({ formDefinition }: { formDefinition: FormDefinition }) {
  const fields = toFormFieldConfigs(formDefinition);
  const initialValues = getInitialValues(formDefinition);

  const handleSubmit = (values: Record<string, unknown>) => {
    console.log('Form submitted:', values);
  };

  return (
    <DynamicForm
      fields={fields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      columns={formDefinition.settings.columns}
      gap={formDefinition.settings.gap}
      submitLabel={formDefinition.settings.submitLabel}
    />
  );
}`}
                </Code>
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
